import type { Nation } from "@/types/api.types";

interface EliminatedNationsProps {
  grouped: Record<string, Nation[]>;
}

const PHASE_ORDER = [
  "GROUP_STAGE",
  "ROUND_OF_32",
  "ROUND_OF_16",
  "QUARTER_FINAL",
  "SEMI_FINAL",
  "THIRD_PLACE",
  "FINAL",
];

const formatPhase = (phase: string) =>
  phase.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function EliminatedNations({ grouped }: EliminatedNationsProps) {
  // Sort phases in tournament order, not JS object insertion order
  const phases = Object.keys(grouped).sort(
    (a, b) => PHASE_ORDER.indexOf(a) - PHASE_ORDER.indexOf(b),
  );
  const totalEliminated = Object.values(grouped).flat().length;

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800">❌ Eliminated Nations</h3>
        <span className="text-sm font-bold text-red-600 bg-red-50 px-2 py-1 rounded-full">
          {totalEliminated} eliminated
        </span>
      </div>

      <div className="space-y-4 max-h-80 overflow-y-auto">
        {phases.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-4">
            No nations eliminated yet
          </p>
        ) : (
          phases.map((phase) => (
            <div key={phase}>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                {formatPhase(phase)}
              </p>
              <div className="space-y-1">
                {grouped[phase].map((nation) => (
                  <div
                    key={nation.id}
                    className="flex items-center gap-3 p-2 rounded-lg bg-red-50 opacity-75"
                  >
                    {nation.flagUrl ? (
                      <img
                        src={nation.flagUrl}
                        className="w-8 h-5 object-cover rounded border grayscale"
                        alt=""
                      />
                    ) : (
                      <div className="w-8 h-5 bg-gray-200 rounded border" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600 line-through">
                        {nation.name}
                      </p>
                      <p className="text-xs text-gray-400">{nation.code}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
